import { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Calendar, LogOut, Search, Crown, Mail, Settings, TrendingUp,
  UserCheck, FileText, Eye, Trash2, Smartphone, MessageCircle, Bell, Loader2, Ticket
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ admin, onLogout }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Remindora'
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [expoUrl, setExpoUrl] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [apkUrl, setApkUrl] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ total: 0, new: 0, read: 0, resolved: 0 });
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationUrl, setNotificationUrl] = useState('/dashboard');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    event_type: 'concert',
    city: 'İstanbul',
    venue: '',
    start_date: '',
    end_date: '',
    description: '',
    url: '',
    price_info: '',
    image_url: ''
  });

  useEffect(() => {
    document.title = 'Admin Panel - Remindora';
    fetchStats();
    fetchUsers();
    fetchSMTPSettings();
    fetchEmailTemplates();
    fetchFeedbacks();
    fetchEvents();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('İstatistikler yüklenemedi');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users?search=${search}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchSMTPSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/smtp-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmtpSettings(response.data);
    } catch (error) {
      console.error('SMTP ayarları yüklenemedi');
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplate(response.data[0]);
      }
    } catch (error) {
      console.error('Email şablonları yüklenemedi');
    }
  };


  const fetchFeedbacks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/feedbacks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbacks(response.data);
      
      // Calculate stats
      const stats = {
        total: response.data.length,
        new: response.data.filter(f => f.status === 'new').length,
        read: response.data.filter(f => f.status === 'read').length,
        resolved: response.data.filter(f => f.status === 'resolved').length
      };
      setFeedbackStats(stats);
    } catch (error) {
      console.error('Geribildirimleryüklenemedi');
    }
  };

  const updateFeedbackStatus = async (feedbackId, status, adminNote = null) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API}/admin/feedbacks/${feedbackId}/status`,
        null,
        {
          params: { status, admin_note: adminNote },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Durum güncellendi');
      fetchFeedbacks();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!confirm('Bu geribildirimi silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/feedbacks/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Geribildirim silindi');
      fetchFeedbacks();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Events fetch error:', error);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Etkinlik güncellendi');
      } else {
        await axios.post(`${API}/admin/events`, eventForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Etkinlik eklendi');
      }
      
      fetchEvents();
      setEventDialogOpen(false);
      resetEventForm();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm('Bu etkinliği silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Etkinlik silindi');
      fetchEvents();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const editEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      event_type: event.event_type,
      city: event.city,
      venue: event.venue,
      start_date: event.start_date,
      end_date: event.end_date || '',
      description: event.description || '',
      url: event.url || '',
      price_info: event.price_info || '',
      image_url: event.image_url || ''
    });
    setEventDialogOpen(true);
  };

  const resetEventForm = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      event_type: 'concert',
      city: 'İstanbul',
      venue: '',
      start_date: '',
      end_date: '',
      description: '',
      url: '',
      price_info: '',
      image_url: ''
    });
  };

  const updateUserType = async (userId, userType) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/users/${userId}`,
        { user_type: userType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kullanıcı tipi güncellendi');
      fetchUsers();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`"${userEmail}" kullanıcısını ve TÜM verilerini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kullanıcı başarıyla silindi');
      fetchUsers();
      fetchStats(); // Refresh stats after deletion
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kullanıcı silinemedi');
    }
  };

  const saveSMTPSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/smtp-settings`, smtpSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SMTP ayarları kaydedildi');
    } catch (error) {
      toast.error('SMTP ayarları kaydedilemedi');
    }
  };

  const saveEmailTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/email-templates`, selectedTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email şablonu kaydedildi');
      fetchEmailTemplates();
    } catch (error) {
      toast.error('Şablon kaydedilemedi');
    }
  };

  const getPreviewHTML = () => {
    if (!selectedTemplate) return '';
    
    // Replace variables with sample data
    let html = selectedTemplate.body;
    html = html.replace(/{user_name}/g, 'Kadir');
    html = html.replace(/{partner_name}/g, 'Selin');
    html = html.replace(/{date_title}/g, 'Nişan Günümüz');
    html = html.replace(/{days_left}/g, '7');
    
    return html;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
              <p className="text-sm text-slate-600">{admin.email}</p>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="stats" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto text-xs">
            <TabsTrigger value="stats" className="gap-1">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden lg:inline">İstatistikler</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-1">
              <Users className="w-4 h-4" />
              <span className="hidden lg:inline">Kullanıcılar</span>
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-1">
              <Ticket className="w-4 h-4" />
              <span className="hidden lg:inline">Etkinlikler</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-1">
              <Bell className="w-4 h-4" />
              <span className="hidden lg:inline">Bildirim</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-1">
              <FileText className="w-4 h-4" />
              <span className="hidden lg:inline">Şablonlar</span>
            </TabsTrigger>
            <TabsTrigger value="feedback" className="gap-1">
              <MessageCircle className="w-4 h-4" />
              <span className="hidden lg:inline">Geri Bildirim</span>
            </TabsTrigger>
          </TabsList>

          {/* Statistics Tab */}
          <TabsContent value="stats" className="space-y-6">
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">
                      Toplam Kullanıcı
                    </CardTitle>
                    <Users className="w-4 h-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.total_users}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">
                      Bugün Kayıt Olanlar
                    </CardTitle>
                    <UserCheck className="w-4 h-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.today_users}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">
                      Premium Kullanıcılar
                    </CardTitle>
                    <Crown className="w-4 h-4 text-yellow-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.premium_users}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">
                      Toplam Özel Günler
                    </CardTitle>
                    <Calendar className="w-4 h-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{stats.total_dates}</div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Kullanıcılar</CardTitle>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Ara..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-64"
                    />
                    <Button onClick={fetchUsers} size="icon">
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Partner Adı</TableHead>
                      <TableHead>Şehir</TableHead>
                      <TableHead>Kayıt Tarihi</TableHead>
                      <TableHead>Tarih Sayısı</TableHead>
                      <TableHead>Tip</TableHead>
                      <TableHead>Aksiyon</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{user.partner_name}</TableCell>
                        <TableCell>{user.profile?.city || '-'}</TableCell>
                        <TableCell>
                          {new Date(user.created_at).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>{user.date_count}</TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              user.user_type === 'premium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {user.user_type === 'premium' ? 'Premium' : 'Normal'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select
                              value={user.user_type || 'normal'}
                              onValueChange={(value) => updateUserType(user.id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="premium">Premium</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="destructive"
                              size="icon"
                              onClick={() => deleteUser(user.id, user.email)}
                              title="Kullanıcıyı Sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Etkinlik Yönetimi
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      const token = localStorage.getItem('admin_token');
                      const response = await fetch(`${API}/admin/events/template`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'etkinlikler_sablonu.xlsx';
                      a.click();
                      toast.success('Şablon indirildi!');
                    }}
                  >
                    📥 Excel Şablonu İndir
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('excel-upload').click()}
                  >
                    📤 Excel Yükle
                  </Button>
                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const token = localStorage.getItem('admin_token');
                        const response = await axios.post(`${API}/admin/events/upload`, formData, {
                          headers: { 
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                          }
                        });
                        toast.success(`${response.data.added_count} etkinlik eklendi!`);
                        fetchEvents();
                        e.target.value = '';
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Yükleme başarısız');
                      }
                    }}
                  />
                  <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => { resetEventForm(); setEventDialogOpen(true); }}>
                        Yeni Etkinlik Ekle
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingEvent ? 'Etkinliği Düzenle' : 'Yeni Etkinlik Ekle'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEventSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label>Başlık *</Label>
                          <Input
                            value={eventForm.title}
                            onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                            required
                            placeholder="Örn: Sezen Aksu Konseri"
                          />
                        </div>
                        
                        <div>
                          <Label>Etkinlik Türü *</Label>
                          <Select value={eventForm.event_type} onValueChange={(value) => setEventForm({...eventForm, event_type: value})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cinema">🎬 Sinema</SelectItem>
                              <SelectItem value="theater">🎭 Tiyatro</SelectItem>
                              <SelectItem value="concert">🎵 Konser</SelectItem>
                              <SelectItem value="musical">🎶 Müzikal</SelectItem>
                              <SelectItem value="exhibition">🎨 Sergi/Bienal</SelectItem>
                              <SelectItem value="other">⭐ Diğer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Şehir *</Label>
                          <Select value={eventForm.city} onValueChange={(value) => setEventForm({...eventForm, city: value})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="İstanbul">İstanbul</SelectItem>
                              <SelectItem value="Ankara">Ankara</SelectItem>
                              <SelectItem value="İzmir">İzmir</SelectItem>
                              <SelectItem value="Antalya">Antalya</SelectItem>
                              <SelectItem value="Bursa">Bursa</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="col-span-2">
                          <Label>Mekan *</Label>
                          <Input
                            value={eventForm.venue}
                            onChange={(e) => setEventForm({...eventForm, venue: e.target.value})}
                            required
                            placeholder="Örn: Zorlu PSM"
                          />
                        </div>

                        <div>
                          <Label>Başlangıç Tarihi *</Label>
                          <Input
                            type="date"
                            value={eventForm.start_date}
                            onChange={(e) => setEventForm({...eventForm, start_date: e.target.value})}
                            required
                          />
                        </div>

                        <div>
                          <Label>Bitiş Tarihi</Label>
                          <Input
                            type="date"
                            value={eventForm.end_date}
                            onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                          />
                        </div>

                        <div className="col-span-2">
                          <Label>Açıklama</Label>
                          <Textarea
                            value={eventForm.description}
                            onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                            placeholder="Etkinlik hakkında detaylar..."
                            rows={3}
                          />
                        </div>

                        <div>
                          <Label>Bilet URL</Label>
                          <Input
                            value={eventForm.url}
                            onChange={(e) => setEventForm({...eventForm, url: e.target.value})}
                            placeholder="https://biletix.com/..."
                          />
                        </div>

                        <div>
                          <Label>Fiyat Bilgisi</Label>
                          <Input
                            value={eventForm.price_info}
                            onChange={(e) => setEventForm({...eventForm, price_info: e.target.value})}
                            placeholder="Örn: 150-500 TL"
                          />
                        </div>

                        <div className="col-span-2">
                          <Label>Görsel URL</Label>
                          <Input
                            value={eventForm.image_url}
                            onChange={(e) => setEventForm({...eventForm, image_url: e.target.value})}
                            placeholder="https://..."
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button type="submit" className="flex-1">
                          {editingEvent ? 'Güncelle' : 'Ekle'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => { setEventDialogOpen(false); resetEventForm(); }}>
                          İptal
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Başlık</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Şehir</TableHead>
                      <TableHead>Mekan</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.title}</TableCell>
                        <TableCell>
                          {event.event_type === 'cinema' && '🎬'}
                          {event.event_type === 'theater' && '🎭'}
                          {event.event_type === 'concert' && '🎵'}
                          {event.event_type === 'musical' && '🎶'}
                          {event.event_type === 'exhibition' && '🎨'}
                          {event.event_type === 'other' && '⭐'}
                          {' '}{event.event_type}
                        </TableCell>
                        <TableCell>{event.city}</TableCell>
                        <TableCell>{event.venue}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(event.start_date).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {event.is_active ? 'Aktif' : 'Pasif'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="outline" size="icon" onClick={() => editEvent(event)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="destructive" size="icon" onClick={() => deleteEvent(event.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {events.length === 0 && (
                  <div className="text-center py-8 text-neutral-500">
                    Henüz etkinlik eklenmedi
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Push Bildirim Gönder
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* User Selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Kullanıcı Seçimi *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const usersWithPush = users.filter(u => u.push_subscription);
                        if (selectedUsers.length === usersWithPush.length) {
                          setSelectedUsers([]);
                        } else {
                          setSelectedUsers(usersWithPush.map(u => u.id));
                        }
                      }}
                    >
                      {selectedUsers.length === users.filter(u => u.push_subscription).length && users.filter(u => u.push_subscription).length > 0
                        ? 'Tümünü Kaldır'
                        : 'Tümünü Seç'}
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-neutral-50">
                    {users.length === 0 ? (
                      <p className="text-sm text-neutral-500 text-center py-4">Kullanıcı yok</p>
                    ) : (
                      <div className="space-y-2">
                        {users.map((user) => {
                          const hasPush = user.push_subscription;
                          return (
                            <label
                              key={user.id}
                              className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-neutral-100 ${
                                !hasPush ? 'opacity-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                  if (!hasPush) {
                                    toast.error('Bu kullanıcının bildirimi kapalı');
                                    return;
                                  }
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, user.id]);
                                  } else {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                  }
                                }}
                                disabled={!hasPush}
                                className="w-4 h-4 rounded border-blue-300 text-blue-600"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium">{user.email}</p>
                                <p className="text-xs text-neutral-500">
                                  {user.partner_name || 'Partner adı yok'} 
                                  {hasPush ? ' 🔔' : ' (Bildirim kapalı)'}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-neutral-600">
                    {selectedUsers.length} kullanıcı seçildi • {users.filter(u => u.push_subscription).length} kullanıcının bildirimi açık
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Bilgi:</strong> Push bildirimleri açık olan kullanıcılara bildirim gönderilecektir. 
                    Bu bildirimler uygulama kapalıyken bile kullanıcılara ulaşır.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notif-title">Bildirim Başlığı *</Label>
                  <Input
                    id="notif-title"
                    placeholder="Örn: Yeni Özellikler Eklendi!"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    maxLength={50}
                  />
                  <p className="text-xs text-neutral-500">{notificationTitle.length}/50</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notif-body">Bildirim Mesajı *</Label>
                  <Textarea
                    id="notif-body"
                    placeholder="Bildirim içeriğini yazın..."
                    value={notificationBody}
                    onChange={(e) => setNotificationBody(e.target.value)}
                    rows={4}
                    maxLength={200}
                  />
                  <p className="text-xs text-neutral-500">{notificationBody.length}/200</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notif-url">Hedef Sayfa (Opsiyonel)</Label>
                  <Input
                    id="notif-url"
                    placeholder="/dashboard"
                    value={notificationUrl}
                    onChange={(e) => setNotificationUrl(e.target.value)}
                  />
                  <p className="text-xs text-neutral-500">Bildirime tıklayınca açılacak sayfa</p>
                </div>

                <Button
                  onClick={async () => {
                    if (!notificationTitle || !notificationBody) {
                      toast.error('Lütfen başlık ve mesaj girin');
                      return;
                    }
                    
                    if (selectedUsers.length === 0) {
                      toast.error('Lütfen en az bir kullanıcı seçin');
                      return;
                    }

                    setSendingNotification(true);
                    try {
                      const token = localStorage.getItem('admin_token');
                      
                      const response = await axios.post(
                        `${API}/admin/send-push-notification-selected`,
                        {
                          title: notificationTitle,
                          body: notificationBody,
                          url: notificationUrl || '/dashboard',
                          user_ids: selectedUsers
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );

                      toast.success(`✅ ${response.data.sent_count} kullanıcıya push bildirim gönderildi!`);
                      setNotificationTitle('');
                      setNotificationBody('');
                      setNotificationUrl('/dashboard');
                      setSelectedUsers([]);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Bildirim gönderilemedi');
                    } finally {
                      setSendingNotification(false);
                    }
                  }}
                  disabled={sendingNotification || !notificationTitle || !notificationBody}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {sendingNotification ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Bildirim Gönder
                    </>
                  )}
                </Button>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-amber-800">
                    <strong>⚠️ Dikkat:</strong> Bu özellik sadece push notification'a abone olmuş kullanıcılara gönderir. 
                    Bildirimleri kapatmış veya abone olmamış kullanıcılara bildirim gitmez.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* SMTP Tab */}
          <TabsContent value="smtp" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>SMTP Ayarları</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      value={smtpSettings.host}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={smtpSettings.port}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kullanıcı Adı</Label>
                    <Input
                      value={smtpSettings.username}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Şifre</Label>
                    <Input
                      type="password"
                      value={smtpSettings.password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Gönderen Email</Label>
                    <Input
                      type="email"
                      value={smtpSettings.from_email}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Gönderen Ad</Label>
                    <Input
                      value={smtpSettings.from_name}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                    />
                  </div>
                </div>
                <Button onClick={saveSMTPSettings} className="w-full">
                  SMTP Ayarlarını Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Şablonları</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select
                  value={selectedTemplate?.template_id}
                  onValueChange={(value) => {
                    const template = emailTemplates.find((t) => t.template_id === value);
                    setSelectedTemplate(template);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Şablon seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map((template) => (
                      <SelectItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedTemplate && (
                  <>
                    <div className="space-y-2">
                      <Label>Şablon Adı</Label>
                      <Input
                        value={selectedTemplate.name}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Konu (Email başlığı)</Label>
                      <Input
                        value={selectedTemplate.subject}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })
                        }
                      />
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="font-medium text-blue-900 mb-2">📝 Değişkenler:</p>
                      <div className="space-y-1 text-sm text-blue-800">
                        <p><code className="bg-blue-100 px-2 py-1 rounded">{'{user_name}'}</code> - Kullanıcının adı (örn: Kadir)</p>
                        <p><code className="bg-blue-100 px-2 py-1 rounded">{'{partner_name}'}</code> - Partner adı (örn: Selin)</p>
                        {selectedTemplate.template_id === 'reminder' && (
                          <>
                            <p><code className="bg-blue-100 px-2 py-1 rounded">{'{date_title}'}</code> - Özel gün adı (örn: Nişan Günümüz)</p>
                            <p><code className="bg-blue-100 px-2 py-1 rounded">{'{days_left}'}</code> - Kalan gün sayısı</p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>İçerik (HTML)</Label>
                      <Textarea
                        value={selectedTemplate.body}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, body: e.target.value })
                        }
                        rows={15}
                        className="font-mono text-xs"
                      />
                      <p className="text-xs text-slate-500">
                        💡 HTML kullanabilirsiniz. Renkler, stiller ve emojiler ekleyin!
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        onClick={() => setPreviewOpen(true)} 
                        variant="outline"
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        👁️ Önizleme
                      </Button>
                      <Button 
                        onClick={saveEmailTemplate} 
                        className="bg-blue-600 hover:bg-blue-700 gap-2"
                      >
                        💾 Kaydet
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mobile QR Tab */}
          <TabsContent value="mobile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-6 h-6" />
                  Mobil Uygulama İndirme
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* APK Download Section */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-6">
                  <div className="text-center space-y-4">
                    <div className="text-6xl">📱</div>
                    <h3 className="text-2xl font-bold text-purple-900">
                      Android APK İndir
                    </h3>
                    <p className="text-gray-600">
                      Mobil uygulamayı direkt telefonunuza indirin
                    </p>
                    
                    {apkUrl ? (
                      <div className="space-y-3">
                        <a
                          href={apkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all transform hover:scale-105"
                        >
                          📥 APK İndir (Android)
                        </a>
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <p className="text-sm text-gray-600 break-all">
                            <strong>Link:</strong> {apkUrl}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-800">
                          ⚠️ APK henüz build edilmedi. Aşağıdaki adımları takip edin.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Build Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="font-bold text-blue-900 mb-4 text-lg">🔨 APK Nasıl Build Edilir?</h3>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-2">Adım 1: Terminal'de</p>
                      <code className="block bg-gray-800 text-green-400 p-3 rounded text-sm overflow-x-auto">
                        cd mobile<br/>
                        npm install -g eas-cli<br/>
                        eas login
                      </code>
                      <p className="text-xs text-gray-600 mt-2">
                        Not: Expo hesabınızla giriş yapın (ücretsiz: expo.dev)
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-2">Adım 2: Build Başlat</p>
                      <code className="block bg-gray-800 text-green-400 p-3 rounded text-sm overflow-x-auto">
                        eas build --platform android --profile preview
                      </code>
                      <p className="text-xs text-gray-600 mt-2">
                        ⏳ Build 15-20 dakika sürer
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-2">Adım 3: APK Linkini Al</p>
                      <p className="text-sm text-gray-700">
                        Build bitince terminal'de bir link verilecek:<br/>
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                          https://expo.dev/artifacts/...apk
                        </code>
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        Bu linki aşağıya yapıştırın!
                      </p>
                    </div>
                  </div>
                </div>

                {/* APK URL Input */}
                <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                  <h3 className="font-bold text-purple-900 mb-4">📎 APK Linkini Ekle</h3>
                  <div className="space-y-3">
                    <Input
                      type="text"
                      placeholder="https://expo.dev/artifacts/eas/..."
                      value={apkUrl}
                      onChange={(e) => setApkUrl(e.target.value)}
                      className="text-sm"
                    />
                    <Button
                      onClick={() => {
                        if (apkUrl.trim()) {
                          toast.success('APK linki kaydedildi! Artık yukarıdan indirebilirsiniz.');
                        } else {
                          toast.error('Lütfen APK linkini girin');
                        }
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      💾 APK Linkini Kaydet
                    </Button>
                    <p className="text-xs text-gray-500">
                      Build bittiğinde verilen linki buraya yapıştırın
                    </p>
                  </div>
                </div>

                {/* Installation Instructions */}
                {/* Installation Instructions */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-900 mb-2">📲 APK Kurulumu (Android)</h3>
                  <ol className="space-y-2 text-sm text-yellow-800">
                    <li><strong>1.</strong> Yukarıdaki "APK İndir" butonuna tıklayın</li>
                    <li><strong>2.</strong> APK dosyası indirilecek</li>
                    <li><strong>3.</strong> Telefonda "Bilinmeyen kaynaklardan yükleme" iznini verin</li>
                    <li><strong>4.</strong> APK'yı açın ve yükleyin</li>
                    <li><strong>5.</strong> Remindora app'i artık telefonunuzda!</li>
                  </ol>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">✅ Avantajlar</h3>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>✓ Expo Go gerektirmez</li>
                    <li>✓ Offline çalışır</li>
                    <li>✓ Native performans</li>
                    <li>✓ Gerçek uygulama deneyimi</li>
                    <li>✓ Play Store'a da yüklenebilir</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Email Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📧 Email Önizleme</DialogTitle>
            <p className="text-sm text-slate-600">
              Örnek verilerle nasıl görüneceğini inceleyin
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Email Subject Preview */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-600 mb-1">Konu:</p>
              <p className="font-semibold text-slate-900">
                {selectedTemplate?.subject
                  .replace(/{user_name}/g, 'Kadir')
                  .replace(/{partner_name}/g, 'Selin')
                  .replace(/{date_title}/g, 'Nişan Günümüz')
                  .replace(/{days_left}/g, '7')}
              </p>
            </div>

            {/* Sample Data Info */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900 mb-2">
                📝 Önizleme örnek verileri:
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                <div>• Kullanıcı: <strong>Kadir</strong></div>
                <div>• Partner: <strong>Selin</strong></div>
                <div>• Özel Gün: <strong>Nişan Günümüz</strong></div>
                <div>• Kalan Gün: <strong>7</strong></div>
              </div>
            </div>

            {/* Email HTML Preview */}
            <div className="border rounded-lg overflow-hidden bg-white">
              <iframe
                srcDoc={getPreviewHTML()}
                className="w-full h-[600px] border-0"
                title="Email Preview"
                sandbox="allow-same-origin"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Kapat
              </Button>
              <Button 
                onClick={() => {
                  setPreviewOpen(false);
                  saveEmailTemplate();
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                💾 Kaydet ve Kapat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
